import React, {useState} from 'react';

import * as Analytics from 'analytics/Analytics';
import {AnalyticsMetadataForContext, ModalMetadataEntryType} from 'analytics/AnalyticsTypes';
import {assert} from 'Asserts';
import {ANALYTICS_MODULE, COMMON_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {CICOItemIdsToAssignee, SampleStatusItem} from 'constants/SampleStatusConstants';
import * as Context from 'Context';
import AssigneeFormGroup from 'inventory/check-in-check-out/AssigneeFormGroup';
import CICOValidationLabel from 'inventory/check-in-check-out/CICOValidationLabel';
import CommentFormGroup from 'inventory/check-in-check-out/CommentFormGroup';
import {
  canProceedWithSamples,
  checkForSampleStatusWarnings,
  SampleStatusWarning,
} from 'inventory/sample-status/SampleStatusWarning';
import {BaseSampleStatusWarningTrackingParams} from 'inventory/sample-status/SampleStatusWarningTracking';
import {Party} from 'parties/PartyConstants';
import EntityStore from 'stores/EntityStore';
import {ButtonStyle} from 'ui-library/buttons/Button';
import useFormInput from 'ui-library/hooks/useFormInput';
import useOnMount from 'ui-library/hooks/useOnMount';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import Modal from 'ui-library/modals/Modal';
import AddToWorklistForm, {useAddToWorklistForm, WorklistFormSelectorType} from 'worklists/AddToWorklistForm';
import {WorklistType} from 'worklists/WorklistConstants';

export interface BaseCheckoutModalProps {
  items: Array<SampleStatusItem>;
  onRequestClose: () => void;
  onCheckOut: (itemIdsToAssignee: CICOItemIdsToAssignee, comment?: string) => void;
  shouldWarnIfReservedForCurrentUser?: boolean;
  statusWarningTrackingParams: BaseSampleStatusWarningTrackingParams;
  analyticsMetadata?: AnalyticsMetadataForContext;
  shouldAlwaysShowSampleStatusTable?: boolean;
  title: string;
  /**
   * Provide an alternative way to set the assignees based on the provided map
   * and labelled by the provided label.
   */
  alternateAssigneeConfig?: {
    label: string;
    // Items may be a subset of those provided in props.items if there are items that cannot be checked out
    // this way, but may still be checked out when assignee is selected manually
    validAssigneeByItemId: CICOItemIdsToAssignee;
    getWarning?: (selectedItemIds: Set<string>) => JSX.Element | null;
    analyticsMetadataEntryType?: ModalMetadataEntryType;
  };
}

export default function BaseCheckOutModal(props: BaseCheckoutModalProps): JSX.Element {
  const itemIds = [...new Set(props.items.map((item) => item.id))];
  const [assignee, setAssignee] = useState<Party | null>(EntityStore.currentUser());
  const [useAlternateAssignees, setUseAlternateAssignees] = useState<boolean>(
    props.alternateAssigneeConfig != null
  );
  const commentFormInput = useFormInput('');
  const worklistFormSettings = {
    autoFocus: false,
    ids: itemIds,
    worklistType: WorklistType.CONTAINER,
    refreshOptions: {
      refreshWorklistItemsSearch: true,
      refreshWorklistsSearch: true,
      refreshRecentWorklists: true,
    },
  };
  const worklistFormState = useAddToWorklistForm(worklistFormSettings, null);

  const [canProceedOnSampleStatus, setCanProceedOnSampleStatus] = useState(false);
  const [selectedSampleIds, setSelectedSampleIds] = useState<Array<string>>([]);

  const [shouldShowStatusWarnings, setShouldShowStatusWarnings] = useState(false);
  const [loading, setLoading] = useState(true);
  const [samples, setSamples] = useState<Array<SampleStatusItem> | null>(null);

  const allowedSelectedSampleIds = useAlternateAssignees
    ? Object.keys(props.alternateAssigneeConfig?.validAssigneeByItemId ?? {}).filter((id) =>
        selectedSampleIds.includes(id)
      )
    : selectedSampleIds;
  const isSubmitEnabled =
    (useAlternateAssignees || Boolean(assignee)) &&
    canProceedWithSamples(allowedSelectedSampleIds, shouldShowStatusWarnings, canProceedOnSampleStatus);

  useOnMount(() => {
    async function checkForWarnings(): Promise<void> {
      const {shouldShowWarning, samples} = await checkForSampleStatusWarnings({
        sampleIds: itemIds,
        shouldAlwaysShowWarning: props.shouldAlwaysShowSampleStatusTable,
      });
      setShouldShowStatusWarnings(shouldShowWarning);
      setSamples(samples ?? null);
      setSelectedSampleIds(itemIds);
      setLoading(false);
    }
    checkForWarnings();
    Analytics.trackEventWithModule(
      COMMON_ANALYTICS_ACTIONS.OPENED_MODAL,
      ANALYTICS_MODULE.SAMPLES,
      props.analyticsMetadata
    );
  });

  const possibleWarning =
    shouldShowStatusWarnings && samples != null ? (
      <SampleStatusWarning
        sampleIds={itemIds}
        selectedSampleIds={selectedSampleIds}
        setSelectedSampleIds={setSelectedSampleIds}
        shouldProceed={canProceedOnSampleStatus}
        setShouldProceed={setCanProceedOnSampleStatus}
        trackingParams={props.statusWarningTrackingParams}
        samples={samples}
      />
    ) : null;

  const onRequestSubmit = (): void => {
    let itemIdsToAssignee: CICOItemIdsToAssignee;
    if (useAlternateAssignees) {
      assert(props.alternateAssigneeConfig != null, 'Map of alternate assignees needed');
      // eslint-disable-next-line benchlint/no-spread-reduce
      itemIdsToAssignee = allowedSelectedSampleIds.reduce(
        (obj, itemId) => ({
          ...obj,
          [itemId]: props.alternateAssigneeConfig!.validAssigneeByItemId[itemId],
        }),
        {}
      );
    } else {
      // eslint-disable-next-line benchlint/no-spread-reduce
      itemIdsToAssignee = selectedSampleIds.reduce((obj, itemId) => ({...obj, [itemId]: assignee!}), {});
    }
    Context.usingAsync({analyticsMetadata: props.analyticsMetadata}, null, () => {
      props.onCheckOut(itemIdsToAssignee, commentFormInput.value);
    });

    const analyticsMetadataForSubmit = {
      ...props.analyticsMetadata,
      ...(props.alternateAssigneeConfig != null && {
        modalMetadataEntryInfo: [
          {
            entryType: useAlternateAssignees
              ? props.alternateAssigneeConfig.analyticsMetadataEntryType
              : ModalMetadataEntryType.MANUAL,
            metadataElement: 'cico_assignee',
          },
        ],
      }),
    };
    Analytics.trackEventWithModule(
      COMMON_ANALYTICS_ACTIONS.SUBMITTED_MODAL,
      ANALYTICS_MODULE.SAMPLES,
      analyticsMetadataForSubmit
    );

    // If the user didn't fill out the worklist section of the form, we assume the intent is to not add
    // the items to a worklist
    if (!worklistFormState.isDisabled) {
      worklistFormState.addItems(allowedSelectedSampleIds);
    }
    props.onRequestClose();
  };

  const onRequestClose = (): void => {
    Analytics.trackEventWithModule(
      COMMON_ANALYTICS_ACTIONS.CLOSED_MODAL,
      ANALYTICS_MODULE.SAMPLES,
      props.analyticsMetadata
    );
    props.onRequestClose();
  };

  return (
    <Modal
      buttons={[
        {
          disabled: !isSubmitEnabled,
          style: ButtonStyle.SECONDARY,
          submit: true,
          text: props.title,
        },
      ]}
      footer={
        !shouldShowStatusWarnings && (
          <CICOValidationLabel
            items={props.items}
            shouldWarnIfCheckedOutForCurrentUser={true}
            shouldWarnIfReservedForCurrentUser={props.shouldWarnIfReservedForCurrentUser}
          />
        )
      }
      footerPullRight={true}
      onRequestClose={onRequestClose}
      onRequestSubmit={onRequestSubmit}
      title={props.title}
      expandHeight={true}
    >
      {loading ? (
        <CenteredHorizontalThrobber />
      ) : (
        <div>
          {possibleWarning}
          {canProceedWithSamples(
            allowedSelectedSampleIds,
            shouldShowStatusWarnings,
            canProceedOnSampleStatus
          ) && (
            <>
              <AssigneeFormGroup
                assignee={assignee}
                onChange={setAssignee}
                alternateAssigneesOption={
                  props.alternateAssigneeConfig && {
                    label: props.alternateAssigneeConfig.label,
                    selected: useAlternateAssignees,
                    setSelected: setUseAlternateAssignees,
                  }
                }
              />
              <CommentFormGroup comment={commentFormInput.value} onChange={commentFormInput.onChange} />
              <AddToWorklistForm
                {...worklistFormState}
                {...worklistFormSettings}
                selectorType={WorklistFormSelectorType.RADIO_GROUP}
                radioGroupLabelText="Add items to worklist"
                chipsAreLinks={false}
                ids={allowedSelectedSampleIds}
              />
              {useAlternateAssignees &&
                props.alternateAssigneeConfig?.getWarning != null &&
                props.alternateAssigneeConfig.getWarning(new Set(selectedSampleIds))}
            </>
          )}
        </div>
      )}
    </Modal>
  );
}
